// seatLayout.js — shared seat-roster layout derivation.
//
// CC_LOBBY_OVERLAY_WINDOW (2026-06-07): extracted from Lobby.js so the
// in-game Lobby overlay window renders the SAME roster the pre-game
// lobby does, from the same broadcast shape, without duplicating the
// layout math. `layoutFromLobbyData` is moved verbatim from Lobby.js's
// `_layoutFromLobbyData` (behavior-preserving — Lobby's forming render
// still calls this exact function, so the pre-game roster is
// pixel-identical). The pre-broadcast `_computeSeatLayout` fallback
// stays in Lobby.js — it's only reachable before any seats[] payload
// has arrived, a state the in-game overlay never starts from (it always
// seeds from the groupHandler cache / a live Members broadcast).

export const SEAT_COUNT = 6;


export function layoutFromLobbyData(lobbyData) {
    // Phase B + Phase C migration repair (2026-05-21): build the slot
    // shape _computeSeatLayout produces, but sourced from Lobby.seats[]
    // (the Phase A model shape). The occupant carries `ready` directly
    // — the legacy `options.ready` dual-write is gone, so flattening
    // here makes the data source explicit and lets SeatRow read it
    // without traversing a stale nested shape.
    //
    // CC_CLOSED_SEAT_BROADCAST: closed seats are server-owned too —
    // read `seat.closed` off the broadcast, never a local Set. A closed
    // seat row carries no real occupant (user IS NULL AND ai IS NULL),
    // so it renders as 'closed' on every client identically.
    const layout = new Array(SEAT_COUNT).fill(null);
    const seats = (lobbyData && lobbyData.seats) || [];
    const aiSeats = seats
        .filter((seat) => seat && seat.user === null && seat.ai)
        .map((seat) => seat.seat_index);
    aiSeats.sort((a, b) => a - b);
    seats.forEach((seat) => {
        if (!seat) return;
        const seatIndex = seat.seat_index;
        if (seatIndex < 0 || seatIndex >= SEAT_COUNT) return;
        if (seat.user) {
            layout[seatIndex] = {
                kind: 'human',
                occupant: {
                    id: seat.user.id,
                    user: seat.user,
                    display_name: seat.display_name || 'Unknown',
                    ready: Boolean(seat.ready),
                    // CC_POSTGAME_ROSTER_FIXES C1: socket-liveness from
                    // SeatSerializer.get_connected. null/undefined means
                    // the field isn't in this payload (rollout window) —
                    // SeatRow treats only an explicit `false` as
                    // disconnected so it never paints live peers as
                    // dropped.
                    connected: seat.connected,
                    // CC_SEAT_IDENTITY_MARKS: per-seat Discord-nick
                    // capture; SeatRow's platform tag reads its presence
                    // as the (heuristic) Discord signal. '' for web seats.
                    platform_display_name: seat.platform_display_name,
                },
            };
        } else if (seat.ai) {
            layout[seatIndex] = {
                kind: 'ai',
                aiIndex: aiSeats.indexOf(seatIndex),
            };
        } else if (seat.closed) {
            layout[seatIndex] = { kind: 'closed' };
        } else {
            // CC_OWNER_FILL_EMPTY_SEAT_AI: an empty, non-closed Seat row
            // present in the broadcast. SeatSerializer.vacated
            // (CC_MIDGAME_SEAT_PRESENCE) tells a patiently-vacated LIVE
            // seat (a player left mid-game; the GamePlayer survives) apart
            // from a never-filled placeholder. Carry it on the open slot
            // so the in-game overlay offers the owner a Fill-with-AI
            // affordance only on a vacated (driveable) seat. A seat with
            // no row at all is filled below as a bare open slot (vacated
            // undefined -> falsy), so no button there.
            layout[seatIndex] = {
                kind: 'open',
                vacated: Boolean(seat.vacated),
            };
        }
    });
    for (let i = 0; i < SEAT_COUNT; i += 1) {
        if (layout[i] === null) {
            layout[i] = { kind: 'open' };
        }
    }
    return layout;
}
